import type { PreChatFormLabels, WidgetConfig } from "../types/messages";

// Default config matching backend defaults
const DEFAULT_CONFIG: WidgetConfig = {
  title: "Chat Support",
  initial_message: "Hello! How can I help you today?",
  placeholder: "Type your message...",
  error_message: "Sorry, something went wrong. Please try again.",
  avatar_url: "https://lizadocs.com/logo.webp",
  anchor: "bottom_right",
  offset_x: 20,
  offset_y: 20,
  theme: "light",
  // Must match the Classic tokens in themes.css — these get written as inline
  // host vars by applyCustomStyles, so a stale value here overrides the theme.
  primary_color: "#2F6FED",
  border_radius: "20px",
  show_sources: false,
};

const ENGLISH_LABELS: PreChatFormLabels = {
  header: "Let us know who you are before we chat:",
  name_placeholder: "Your name",
  email_placeholder: "Your email",
  phone_placeholder: "Your phone (optional)",
  submit: "Send",
  skip: "Skip",
  name_required: "Please enter your name",
  email_invalid: "Please enter a valid email address",
  sending: "Sending...",
};

// Mirrors backend's pre_chat_form_labels() so preview mode can
// derive localized labels from config.language without an API call.
const PRE_CHAT_LABELS_BY_LANGUAGE: Record<string, PreChatFormLabels> = {
  turkish: {
    header: "Sohbete başlamadan önce sizi tanıyalım:",
    name_placeholder: "Adınız",
    email_placeholder: "E-posta adresiniz",
    phone_placeholder: "Telefonunuz (isteğe bağlı)",
    submit: "Gönder",
    skip: "Atla",
    name_required: "Lütfen adınızı girin",
    email_invalid: "Lütfen geçerli bir e-posta adresi girin",
    sending: "Gönderiliyor...",
  },
  german: {
    header: "Lassen Sie uns wissen, wer Sie sind, bevor wir chatten:",
    name_placeholder: "Ihr Name",
    email_placeholder: "Ihre E-Mail",
    phone_placeholder: "Ihre Telefonnummer (optional)",
    submit: "Senden",
    skip: "Überspringen",
    name_required: "Bitte geben Sie Ihren Namen ein",
    email_invalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    sending: "Wird gesendet...",
  },
  french: {
    header: "Faisons connaissance avant de commencer :",
    name_placeholder: "Votre nom",
    email_placeholder: "Votre e-mail",
    phone_placeholder: "Votre téléphone (optionnel)",
    submit: "Envoyer",
    skip: "Passer",
    name_required: "Veuillez entrer votre nom",
    email_invalid: "Veuillez entrer une adresse e-mail valide",
    sending: "Envoi en cours...",
  },
  spanish: {
    header: "Cuéntenos quién es antes de comenzar a chatear:",
    name_placeholder: "Su nombre",
    email_placeholder: "Su correo electrónico",
    phone_placeholder: "Su teléfono (opcional)",
    submit: "Enviar",
    skip: "Omitir",
    name_required: "Por favor ingrese su nombre",
    email_invalid: "Por favor ingrese un correo electrónico válido",
    sending: "Enviando...",
  },
  russian: {
    header: "Расскажите немного о себе перед началом чата:",
    name_placeholder: "Ваше имя",
    email_placeholder: "Ваш email",
    phone_placeholder: "Ваш телефон (необязательно)",
    submit: "Отправить",
    skip: "Пропустить",
    name_required: "Пожалуйста, введите ваше имя",
    email_invalid: "Пожалуйста, введите корректный email",
    sending: "Отправка...",
  },
  belarusian: {
    header: "Раскажыце пра сябе перад пачаткам чата:",
    name_placeholder: "Вашае імя",
    email_placeholder: "Ваш email",
    phone_placeholder: "Ваш тэлефон (неабавязкова)",
    submit: "Адправіць",
    skip: "Прапусціць",
    name_required: "Калі ласка, увядзіце вашае імя",
    email_invalid: "Калі ласка, увядзіце карэктны email",
    sending: "Адпраўка...",
  },
};

/**
 * Resolve pre-chat labels from a language string.
 * Used by preview mode where the backend embed endpoint is not called.
 */
export function preChatLabelsForLanguage(language?: string): PreChatFormLabels {
  if (!language) return ENGLISH_LABELS;
  return PRE_CHAT_LABELS_BY_LANGUAGE[language.toLowerCase()] ?? ENGLISH_LABELS;
}

export interface WidgetEmbedData {
  config: WidgetConfig;
  preChatLabels: PreChatFormLabels;
}

/**
 * Fetch widget configuration from the API.
 * Falls back to defaults if fetch fails.
 */
export async function fetchWidgetConfig(
  widgetId: string,
  apiBaseUrl: string = "https://api.lizadocs.com/api",
): Promise<WidgetEmbedData> {
  const configUrl = `${apiBaseUrl}/embed/${widgetId}`;
  const currentOrigin =
    typeof window !== "undefined" ? window.location.origin : "unknown";

  try {
    const response = await fetch(configUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      console.warn(
        `[LizaDocs] Failed to fetch widget config (status ${response.status})`,
        { widgetId, configUrl, origin: currentOrigin },
      );
      return { config: DEFAULT_CONFIG, preChatLabels: ENGLISH_LABELS };
    }

    const data = await response.json();

    // Merge with defaults to ensure all fields are present
    return {
      config: { ...DEFAULT_CONFIG, ...data.config },
      preChatLabels: data.pre_chat_labels ?? ENGLISH_LABELS,
    };
  } catch (error) {
    console.warn(
      "[LizaDocs] Failed to fetch widget config (network/CORS likely). " +
        "Verify widget allowed_domains includes this exact origin.",
      { widgetId, configUrl, origin: currentOrigin, error },
    );
    return { config: DEFAULT_CONFIG, preChatLabels: ENGLISH_LABELS };
  }
}

/**
 * Merge config from API with any attribute overrides from the custom element.
 */
export function mergeConfig(
  apiConfig: WidgetConfig,
  attributeOverrides: Partial<WidgetConfig>,
): WidgetConfig {
  return {
    ...apiConfig,
    ...Object.fromEntries(
      Object.entries(attributeOverrides).filter(
        ([, v]) => v !== undefined && v !== null,
      ),
    ),
  } as WidgetConfig;
}
